<template>
  <v-container class="px-0">
    <div class="buttonContainer">
      <v-tooltip>
        <template v-slot:activator="{ on, attrs }">
          <v-btn fab small :disabled="!dashboard || loading" @click="() => showNew = true" color="primary" v-bind="attrs" v-on="on">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>New widget</span>
      </v-tooltip>
    </div>
    <v-row class="my-5 d-flex align-center justify-space-between">
     
      <v-col cols="4">
        <Date-picker id="dateFrom" :rules="[rules.required]" :timestamp="dateFrom" @update:timestamp="updateDate" label="Date From" />
      </v-col>

      <v-col cols="4">
        <Date-picker id="dateTo" :rules="[rules.required]" :timestamp="dateTo" @update:timestamp="updateDate" label="Date To" />
      </v-col>

      <v-col cols="4" style="text-align:left">
        <v-btn :disabled="!dashboard || loading" color="success" title="Update" @click="changeFilters">
          <v-icon>mdi-update</v-icon> Update
        </v-btn>
      </v-col>

    </v-row>


    <v-alert v-if="loadError" type="info" outlined>{{ loadError }}</v-alert>

    <WidgetGrid :items="widgets" :deleteWidget="deleteWidget" :filters="{
       dateFrom: this.dateFrom,
       dateTo: this.dateTo,
       ensembles: this.ensembles,
       user: this.userDetail
    }" 
    ref="widget-grid"
    />
    <NewWidget :show="showNew" :onSave="addNewWidget" :onCancel="() => showNew = false" />
  </v-container>
</template>

<script>

import WidgetGrid from '@/components/graphs/WidgetGrid';
import NewWidget from '@/components/graphs/NewWidget';
import DatePicker from '@/components/shared/DatePicker';
import { mapState } from 'vuex';

export default {
  name: 'WidgetsGraphs',
  components: {
    WidgetGrid,
    DatePicker,
    NewWidget
  },
  computed: {
    rules() {
      return this.$script.formRules
    },
    ...mapState([
      'user',
      'plantId'
    ]),
  },
  data() {
    return {
      dashboard: null,
      structureId: null,
      loading: false,
      loadError: null,
      structure: null,
      dateTo: null,
      dateFrom: null,
      breadcrumbsItems: [
        { href: '/home', disabled: false, text: this.$t("home").toUpperCase(), to: '/home' },
        { href: '/widgets_graphs', disabled: true, text: this.$t("widgets_graphs_breadcrumbs").toUpperCase() }
      ],
      widgets: [],
      showNew: false,
      resizeTimeout: null,
      ensembles: [],
      userDetail: {}
    }
  },
  watch: {
    plantId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.load(true)
      }
    }
  },
  created: function () {

    window.addEventListener('resize', this.handleResize);

    this.$store.dispatch('_updateBreadcrumbs', this.breadcrumbsItems);

    let _dateFrom = this.$date.now();
    let _dateTo = this.$date.now().plus({ days: 1 });

    // can manage both formats: 'yyyy-MM-dd' and milliseconds
    if (this.user.sessionParams) {
      if (this.user.sessionParams.dateFrom) {
        // string format yyyy-MM-dd
        if (!isNaN()) {
          _dateFrom = this.$date.fromJSDate(new Date(this.user.sessionParams.dateFrom));
          // timestamp format
        } else {
          _dateFrom = this.$date.fromJSDate(new Date(this.user.sessionParams.dateFrom * 1));
        }
      }

      if (this.user.sessionParams.dateTo) {
        // string format yyyy-MM-dd
        if (!isNaN()) {
          _dateTo = this.$date.fromJSDate(new Date(this.user.sessionParams.dateTo));
          // timestamp format
        } else {
          _dateTo = this.$date.fromJSDate(new Date(this.user.sessionParams.dateTo * 1));
        }
      }
    }

    this.dateFrom = (_dateFrom.toMillis());
    this.dateTo = (_dateTo.toMillis());

    this.load()

  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
  },

  methods: {

    // update date range values
    updateDate(event){
      if(event.id === 'dateFrom') this.dateFrom = event.timestamp;
      if(event.id === 'dateTo') this.dateTo = event.timestamp;
    },

    // reload page data
    // called when page is created, or when an administrator select a new plant 
    async load() {
      const plantId = this.plantId;
      this.loading = true;
      this.loadError = null;
      this.structure = null;
      this.structureId = null;
      this.dashboard = null;
      this.widgets = [];
      this.ensembles = [];
      this.showNew = false;
      try {
        const structure = await this.$store.dispatch('_getStructureFromPlant');
        if (plantId !== this.plantId) return;
        if (!structure || !Number.isInteger(structure.structureId) || structure.structureId <= 0) {
          this.loadError = 'No structure is available for this plant. Graphs require an associated structure.';
          return;
        }
        this.structure = structure;
        this.structureId = structure.structureId;
        this.ensembles = await this.$store.dispatch('_getEnsembles');
        if (plantId !== this.plantId) return;
        this.userDetail = await this.$store.dispatch('_getUser');
        if (plantId !== this.plantId) return;
        await this.loadData();
      } catch (error) {
        if (plantId === this.plantId) this.loadError = 'Unable to load graphs for this plant. Please retry.';
      } finally {
        if (plantId === this.plantId) this.loading = false;
      }
    },

    // called when new date range is provided
    async changeFilters() {
      if (!this.dashboard || !this.structureId || this.loading) return;

      let _toSaveSettings = false;
      let _toUpdateDashboard = false;

      // update date values on settings
      if (this.dateFrom !== this.user.sessionParams.dateFrom || 
          this.dateTo !== this.user.sessionParams.dateTo ||
          this.structureId !== this.user.sessionParams.structureId
        ) {
        _toSaveSettings = true;
        _toUpdateDashboard = true;
      }

      // if I change date range I need to clear widget cache on all the widgets in dashboard
      if(_toSaveSettings){
        await this.$store.dispatch('_deleteWidgetCache', {dashboardId: this.dashboard.dashboardId});
        this.saveUserSettings();
      }

      if(_toUpdateDashboard){
        this.loadData()
      }else{
        this.loadWidgets()
      }
      
    },

    // load widget data
    async loadData() {
      if (!this.structureId) return;

      this.$store.dispatch('_setLoading', true);

      try {
        const dashboards = await this.$store.dispatch('_getDashboards', {
          structureId: this.structureId,
          userId: this.user.id
        });
        if (!Array.isArray(dashboards)) throw new Error('Dashboard response unavailable');
        this.dashboard = dashboards[0] || null;

        // if dashboard is not credated yet, add it first
        if (this.dashboard) {
          await this.loadWidgets();
        } else {
          await this.createDashboard();
        }
      } catch (error) {
        this.loadError = 'Unable to load graphs for this plant. Please retry.';
      }

      this.$store.dispatch('_setLoading', false);
      
    },

    // save user settings
    async saveUserSettings() {
      this.$store.dispatch('_updateUser', {
        data: {
          ...this.user,
          sessionParams: {
            ...this.user.sessionParams || {},
            structureId: this.structureId,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo
          }
        },
        hideMsg: true
      })
    },

    // create new dashboard if not available
    async createDashboard() {
      if (!this.structureId) return;

      const data = {
        structureId: this.structureId,
      }
      const dashboard = await this.$store.dispatch('_createDashboard', data);
      if (!dashboard?.dashboardId) throw new Error('Dashboard creation failed');
      this.dashboard = dashboard;
      await this.loadWidgets();
    },

    // load widget data
    async loadWidgets() {
      if (this.dashboard){
        this.widgets = await this.$store.dispatch('_getWidgets', { dashboardId: this.dashboard.dashboardId });
      }
    },

    async addNewWidget(graphWidgetTypeId) {
      if (!this.dashboard || !this.structureId || this.loading) return;
      this.showNew = false
      const data = {
        dashboardId: this.dashboard.dashboardId,
        graphWidgetTypeId,
        x: 0,
        y: 0,
        height: 4,
        width: 6,
        query: {},
        filter: {},
        params: {}
      }

      const added = await this.$store.dispatch('_addWidget', data);
     
      this.$refs['widget-grid'].addWidget({
        ...added,
      });
    },

    async deleteWidget(widgetId) {
      await this.$store.dispatch('_deleteWidget', widgetId);
      this.$refs['widget-grid'].removeWidget(widgetId);
    },
    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.loadWidgets();
      }, 200); // Ritardo di 200 ms
    },
  }
}
</script>

<style scoped>
.buttonContainer {
  position: fixed;
  top: 76px;
  right: 10px;
  width: 64px;
  text-align: center;
  line-height: 48px;
}
</style>
